import { useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  Dices,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Trophy,
  Clock,
  Minus,
  Coins,
  Gamepad2,
  CircleDollarSign,
  Zap,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

const fmtMoney = (n, sym = "৳") =>
  `${sym}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const FILTERS = [
  { id: "all", label: "All", icon: ClipboardList },
  { id: "won", label: "Won", icon: Trophy },
  { id: "lost", label: "Lost", icon: TrendingDown },
  { id: "pending", label: "Pending", icon: Clock },
];

function resolveResult(b) {
  // Prefer the explicit `outcome` stamped at bet-save time (won/lost/push/pending/void).
  const o = (b.outcome || "").toString().toLowerCase();
  if (o === "won" || o === "push") return "won"; // push displays under "won" with +0 P/L
  if (o === "lost") return "lost";
  if (o === "pending") return "pending";
  if (o === "void") return "lost"; // void → treat as no-result for UI grouping

  // Legacy fallback for older bet docs that only have `status`/`result`.
  const s = (b.status || b.result || "").toString().toLowerCase();
  if (["won", "win", "settled_win"].includes(s)) return "won";
  if (["lost", "lose", "loss", "settled_loss"].includes(s)) return "lost";
  if (["pending", "open", "unsettled", "accepted"].includes(s)) return "pending";
  // Settled rounds (softapi/iGaming) — decide by win amount
  const win = Number(b.win || b.payout || 0);
  const amount = Number(b.amount || b.stake || 0);
  if (s === "settled") return win > 0 ? "won" : "lost";
  // Generic fallback
  if (win > amount && win > 0) return "won";
  if (win === 0 && amount > 0) return "lost";
  return "pending";
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return "—";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function BettingRecordsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/profile/bets");
        if (!cancelled) setItems(data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Light polling — keeps Win/Loss numbers fresh while the user is on the page
    // (game-provider WIN callbacks may settle a round a few seconds after BET).
    const t = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  /* ---- derived aggregates (shown in header pills) ---- */
  const stats = useMemo(() => {
    let staked = 0;
    let payout = 0;
    let wonAmount = 0;
    let lostAmount = 0;
    let won = 0;
    let lost = 0;
    let pending = 0;
    let push = 0;
    items.forEach((b) => {
      const s = Number(b.amount || b.stake || 0);
      const p = Number(b.payout || b.win || 0);
      const np = Number(
        b.net_pnl !== undefined && b.net_pnl !== null ? b.net_pnl : p - s
      );
      staked += s;
      payout += p;
      const r = resolveResult(b);
      if (r === "won") {
        won++;
        if (np > 0) wonAmount += np;
        else push++;
      } else if (r === "lost") {
        lost++;
        lostAmount += Math.abs(np);
      } else pending++;
    });
    const net = payout - staked;
    const winRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;
    return {
      staked,
      payout,
      net,
      won,
      lost,
      pending,
      push,
      wonAmount,
      lostAmount,
      winRate,
      total: items.length,
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((b) => resolveResult(b) === filter);
  }, [items, filter]);

  return (
    <ProfileShell title="Betting records">
      <div className="py-2 pb-10 space-y-4" data-testid="bets-page">
        {/* ---------- Hero Stats ---------- */}
        <div
          className="rounded-3xl relative overflow-hidden p-5 sm:p-6"
          style={{
            background:
              "linear-gradient(135deg, #0d1c15 0%, #0a1119 55%, #0d1c15 100%)",
            border: "1px solid rgba(25,165,92,0.22)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
          }}
        >
          <div
            aria-hidden
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(25,165,92,0.35) 0%, transparent 60%)",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-2.5 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-300">
              <Dices size={11} /> Betting History
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <HeroStat
                label="Total bets"
                value={stats.total.toLocaleString()}
                icon={<ClipboardList size={14} />}
              />
              <HeroStat
                label="Total staked"
                value={fmtMoney(stats.staked)}
                icon={<Coins size={14} />}
              />
              <HeroStat
                label="Net P/L"
                value={`${stats.net >= 0 ? "+" : "-"}${fmtMoney(Math.abs(stats.net))}`}
                tone={stats.net >= 0 ? "emerald" : "red"}
                icon={
                  stats.net >= 0 ? (
                    <TrendingUp size={14} />
                  ) : (
                    <TrendingDown size={14} />
                  )
                }
              />
            </div>

            {/* Won / Lost amount row */}
            {(stats.won > 0 || stats.lost > 0) && (
              <div
                className="mt-3 grid grid-cols-2 gap-2"
                data-testid="bets-amount-row"
              >
                <div
                  className="rounded-xl p-2.5 border flex items-center justify-between"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(25,165,92,0.14) 0%, rgba(25,165,92,0.03) 100%)",
                    borderColor: "rgba(25,165,92,0.28)",
                  }}
                  data-testid="stat-won-amount"
                >
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-300/80">
                    <Trophy size={11} /> Total won
                  </span>
                  <span className="text-emerald-300 font-extrabold text-sm tabular-nums">
                    +{fmtMoney(stats.wonAmount)}
                  </span>
                </div>
                <div
                  className="rounded-xl p-2.5 border flex items-center justify-between"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(244,63,94,0.12) 0%, rgba(244,63,94,0.02) 100%)",
                    borderColor: "rgba(244,63,94,0.25)",
                  }}
                  data-testid="stat-lost-amount"
                >
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-rose-300/80">
                    <TrendingDown size={11} /> Total lost
                  </span>
                  <span className="text-rose-300 font-extrabold text-sm tabular-nums">
                    -{fmtMoney(stats.lostAmount)}
                  </span>
                </div>
              </div>
            )}

            {/* Win-rate bar */}
            {(stats.won > 0 || stats.lost > 0) && (
              <div className="mt-5">
                <div className="flex items-center justify-between text-[11px] text-white/55 mb-1.5">
                  <span className="uppercase tracking-[0.18em] font-bold">
                    Win rate
                  </span>
                  <span className="text-emerald-300 font-black tabular-nums">
                    {stats.winRate}%
                  </span>
                </div>
                <div className="relative h-2 bg-white/5 rounded-full overflow-hidden border border-white/5">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700"
                    style={{
                      width: `${Math.max(2, stats.winRate)}%`,
                      background:
                        "linear-gradient(90deg, #21c46e 0%, #facc15 100%)",
                      boxShadow: "0 0 14px rgba(33,196,110,0.5)",
                    }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-white/40 mt-1.5">
                  <span>🏆 {stats.won} won</span>
                  {stats.pending > 0 && <span>⏳ {stats.pending} pending</span>}
                  <span>💔 {stats.lost} lost</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ---------- Filter chips ---------- */}
        {items.length > 0 && (
          <div
            className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 no-scrollbar"
            data-testid="bets-filters"
          >
            {FILTERS.map((f) => {
              const count =
                f.id === "all"
                  ? stats.total
                  : f.id === "won"
                  ? stats.won
                  : f.id === "lost"
                  ? stats.lost
                  : stats.pending;
              const active = filter === f.id;
              const Icon = f.icon;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`bets-filter-${f.id}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold transition-all shrink-0"
                  style={
                    active
                      ? {
                          background:
                            "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)",
                          color: "white",
                          boxShadow: "0 6px 16px rgba(25,165,92,0.4)",
                        }
                      : {
                          background: "rgba(255,255,255,0.04)",
                          color: "rgba(255,255,255,0.7)",
                          border: "1px solid rgba(255,255,255,0.06)",
                        }
                  }
                >
                  <Icon size={13} />
                  {f.label}
                  <span
                    className="ml-1 px-1.5 h-4 rounded-full text-[10px] font-extrabold tabular-nums inline-flex items-center"
                    style={{
                      background: active
                        ? "rgba(255,255,255,0.22)"
                        : "rgba(255,255,255,0.06)",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* ---------- List / empty / loading ---------- */}
        {loading ? (
          <div className="space-y-2.5" data-testid="bets-loading">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl p-4 bg-white/[0.02] border border-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-white/5 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-2/5 rounded bg-white/5 animate-pulse" />
                    <div className="h-2.5 w-1/5 rounded bg-white/5 animate-pulse" />
                  </div>
                  <div className="h-4 w-16 rounded bg-white/5 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            onPlay={() => navigate("/slots")}
            isFiltered={filter !== "all" && items.length > 0}
          />
        ) : (
          <div className="space-y-2.5" data-testid="bets-list">
            {filtered.map((b, i) => (
              <BetCard key={b.id || b.bet_id || i} bet={b} />
            ))}
          </div>
        )}
      </div>
    </ProfileShell>
  );
}

/* ---------- sub components ---------- */

function HeroStat({ label, value, icon, tone = "default" }) {
  const color =
    tone === "emerald"
      ? "#34d399"
      : tone === "red"
      ? "#fb7185"
      : "#ffffff";
  return (
    <div className="space-y-1">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">
        {icon} {label}
      </div>
      <div
        className="text-[18px] sm:text-xl font-extrabold tabular-nums leading-tight"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

const statusStyle = {
  won: {
    bg: "linear-gradient(135deg, rgba(25,165,92,0.16) 0%, rgba(25,165,92,0.03) 100%)",
    border: "rgba(25,165,92,0.3)",
    chipBg: "rgba(25,165,92,0.2)",
    chipText: "#34d399",
    icon: <Trophy size={11} />,
    label: "Won",
  },
  lost: {
    bg: "linear-gradient(135deg, rgba(244,63,94,0.09) 0%, rgba(244,63,94,0.02) 100%)",
    border: "rgba(244,63,94,0.22)",
    chipBg: "rgba(244,63,94,0.18)",
    chipText: "#fb7185",
    icon: <Minus size={11} />,
    label: "Lost",
  },
  pending: {
    bg: "linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(245,158,11,0.02) 100%)",
    border: "rgba(245,158,11,0.25)",
    chipBg: "rgba(245,158,11,0.2)",
    chipText: "#fbbf24",
    icon: <Clock size={11} />,
    label: "Pending",
  },
};

function BetCard({ bet }) {
  const r = resolveResult(bet);
  const s = statusStyle[r] || statusStyle.pending;
  const stake = Number(bet.amount || bet.stake || 0);
  const payout = Number(bet.payout || bet.win || 0);
  const pl =
    bet.net_pnl !== undefined && bet.net_pnl !== null
      ? Number(bet.net_pnl)
      : payout - stake;
  // Smart game-name fallback: if the backend ever sends just a numeric UID
  // (very old bets, missing db.games row), surface a friendlier "Provider #uid"
  // label so the player never sees a bare number.
  let gameName = bet.game || bet.game_name || bet.title || "Game";
  const uidStr = String(bet.game_uid || "").trim();
  if (typeof gameName === "string" && (gameName.trim() === "" || /^[0-9]+$/.test(gameName.trim()) || gameName === uidStr)) {
    const provDisp = bet.provider || bet.provider_name || bet.provider_code || "";
    gameName = provDisp && uidStr
      ? `${provDisp} · #${uidStr}`
      : uidStr
        ? `Game #${uidStr}`
        : "Game";
  }
  const provider = bet.provider || bet.provider_name || "";

  return (
    <div
      data-testid="bet-card"
      data-bet-result={r}
      className="rounded-2xl p-4 border relative overflow-hidden transition-all hover:border-white/20"
      style={{ background: s.bg, borderColor: s.border }}
    >
      {/* Top row — game + status chip */}
      <div className="flex items-center gap-3">
        <div
          className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background:
              r === "won"
                ? "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)"
                : r === "lost"
                ? "linear-gradient(135deg, #64748b 0%, #334155 100%)"
                : "linear-gradient(135deg, #d97706 0%, #92400e 100%)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15)",
          }}
        >
          <Gamepad2 size={18} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-white font-bold text-sm truncate max-w-[170px]">
              {gameName}
            </span>
            <span
              className="inline-flex items-center gap-1 px-1.5 h-5 rounded-md text-[10px] font-extrabold uppercase tracking-wider"
              style={{ background: s.chipBg, color: s.chipText }}
              data-testid={`bet-status-${r}`}
            >
              {s.icon} {s.label}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[11px] text-white/50">
            {provider && (
              <>
                <span className="truncate max-w-[110px]">{provider}</span>
                <span className="text-white/25">·</span>
              </>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock size={10} />
              {timeAgo(bet.created_at)}
            </span>
            {bet.round_id && (
              <>
                <span className="text-white/25">·</span>
                <span className="font-mono text-[10px] text-white/35">
                  #{String(bet.round_id).slice(-6)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Right summary — Net P/L (large + colored) */}
        <div className="text-right shrink-0">
          {r === "pending" ? (
            <>
              <div className="text-white font-extrabold text-sm tabular-nums">
                ৳{stake.toLocaleString()}
              </div>
              <div className="text-[11px] text-amber-300/80 font-semibold mt-0.5">
                Awaiting
              </div>
            </>
          ) : (
            <div
              className="font-extrabold text-base tabular-nums inline-flex items-center justify-end gap-0.5 leading-none"
              style={{ color: pl > 0 ? "#34d399" : pl < 0 ? "#fb7185" : "#94a3b8" }}
              data-testid="bet-net-pnl"
            >
              {pl > 0 ? (
                <TrendingUp size={13} />
              ) : pl < 0 ? (
                <TrendingDown size={13} />
              ) : (
                <Minus size={13} />
              )}
              {pl > 0 ? "+" : pl < 0 ? "-" : ""}৳
              {Math.abs(pl).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom row — Stake + Payout breakdown */}
      {r !== "pending" && (
        <div
          className="mt-3 pt-2.5 grid grid-cols-2 gap-2 border-t border-white/[0.06]"
          data-testid="bet-amount-breakdown"
        >
          <div className="flex items-center justify-between rounded-lg px-2.5 py-1.5 bg-white/[0.025]">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-white/45">
              <Coins size={10} /> Stake
            </span>
            <span className="text-white/90 font-bold text-[12px] tabular-nums">
              ৳{stake.toLocaleString()}
            </span>
          </div>
          <div
            className="flex items-center justify-between rounded-lg px-2.5 py-1.5"
            style={{
              background:
                r === "won"
                  ? "rgba(25,165,92,0.10)"
                  : "rgba(255,255,255,0.025)",
            }}
          >
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold"
              style={{
                color: r === "won" ? "#86efac" : "rgba(255,255,255,0.45)",
              }}
            >
              <Trophy size={10} /> Payout
            </span>
            <span
              className="font-extrabold text-[12px] tabular-nums"
              style={{
                color: r === "won" ? "#34d399" : "rgba(255,255,255,0.85)",
              }}
              data-testid="bet-payout"
            >
              ৳{payout.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ onPlay, isFiltered }) {
  if (isFiltered) {
    return (
      <div
        className="rounded-2xl p-10 text-center border border-white/5"
        style={{ background: "rgba(255,255,255,0.02)" }}
        data-testid="bets-empty-filtered"
      >
        <Sparkles size={34} className="mx-auto text-white/25" />
        <p className="mt-3 text-white/65 text-sm font-semibold">
          এই filter এ কোনো bet নেই
        </p>
        <p className="mt-1 text-white/40 text-xs">
          অন্য filter চেষ্টা করুন অথবা নতুন bet রাখুন
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-3xl p-8 text-center relative overflow-hidden"
      data-testid="bets-empty"
      style={{
        background:
          "linear-gradient(135deg, rgba(25,165,92,0.1) 0%, rgba(25,165,92,0.02) 100%)",
        border: "1px solid rgba(25,165,92,0.2)",
      }}
    >
      <div
        aria-hidden
        className="absolute -top-16 -right-16 w-52 h-52 rounded-full blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(25,165,92,0.35) 0%, transparent 60%)",
        }}
      />

      <div
        className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mx-auto relative mb-5"
        style={{
          background: "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)",
          boxShadow:
            "0 14px 30px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
      >
        <Dices size={34} className="text-white" />
        <span
          className="absolute -top-1 -right-1 h-6 w-6 rounded-full flex items-center justify-center"
          style={{
            background:
              "linear-gradient(135deg, #fbbf24 0%, #d97706 100%)",
            boxShadow: "0 6px 16px rgba(251,191,36,0.45)",
          }}
        >
          <Zap size={12} className="text-[#1a1408]" />
        </span>
      </div>

      <h3 className="text-white font-extrabold text-xl mb-1.5">
        এখনো কোনো bet রাখা হয়নি
      </h3>
      <p className="text-white/55 text-sm max-w-sm mx-auto leading-relaxed">
        আপনার bet history এখানে দেখা যাবে। ৭২+ provider-এর ১০০০+ game থেকে
        বেছে নিন এবং ১৫০% welcome bonus-এ প্রথম bet রাখুন!
      </p>

      <div className="grid grid-cols-3 gap-2 mt-6 max-w-sm mx-auto">
        {[
          { icon: <Gamepad2 size={14} />, label: "Casino" },
          { icon: <CircleDollarSign size={14} />, label: "Slots" },
          { icon: <Trophy size={14} />, label: "Sports" },
        ].map((x) => (
          <div
            key={x.label}
            className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-white/70 text-[11px] font-semibold"
          >
            <span className="text-emerald-400">{x.icon}</span>
            {x.label}
          </div>
        ))}
      </div>

      <button
        onClick={onPlay}
        data-testid="bets-empty-cta"
        className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-xl font-bold text-sm text-white transition-transform active:scale-95"
        style={{
          background:
            "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)",
          boxShadow:
            "0 12px 28px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
      >
        এখনই খেলা শুরু করুন <ArrowRight size={15} />
      </button>

      <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-amber-300/80 font-semibold">
        <Sparkles size={11} /> প্রথম deposit-এ ১৫০% bonus পাবেন
      </div>
    </div>
  );
}
